import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppLocale } from '@/i18n/context';
import {
  updateConnectionPolicy,
  type ConnectionAccessChoice,
  type ConnectionReadItem,
} from '../connection-read-access/api';
import { useConnectionPeople } from '../connect-access/policy-fields';
import {
  companyRequest,
  emptyNativePostgresConfig,
  nativePostgresChoice,
  nativePostgresConfigBody,
  nativePostgresDetailConfig,
  nativePostgresError,
  nativePostgresAccessChoice,
  secureRequestId,
  type NativePostgresConfig,
  type NativePostgresDetail,
  type NativePostgresTestResult,
} from './model';

export function useNativePostgresSetup({
  open,
  scopeKey,
  existing,
  initialConfig,
  initialAudience,
  onClose,
  onSaved,
  onDeleted,
}: {
  open: boolean;
  scopeKey: string;
  existing?: ConnectionReadItem | null;
  initialConfig?: Partial<NativePostgresConfig>;
  initialAudience?: 'private' | 'company';
  onClose: () => void;
  onSaved?: (item: ConnectionReadItem) => void;
  onDeleted?: (notice?: string) => void;
}) {
  const locale = useAppLocale();
  const people = useConnectionPeople();
  const generation = useRef(0);
  const requestId = useRef<string | null>(null);
  const [config, setConfig] = useState<NativePostgresConfig>({
    ...emptyNativePostgresConfig,
    ...initialConfig,
  });
  const [tested, setTested] = useState<NativePostgresTestResult | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [choice, setChoice] = useState<ConnectionAccessChoice>(
    nativePostgresChoice(existing ?? null, initialAudience),
  );
  const [step, setStep] = useState<'credentials' | 'tables' | 'access' | 'saved'>('credentials');
  const [grantState, setGrantState] = useState<'paused' | 'enabled'>('paused');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [grantError, setGrantError] = useState('');
  const [savedItem, setSavedItem] = useState<ConnectionReadItem | null>(existing ?? null);
  const [query, setQuery] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  useEffect(() => {
    generation.current += 1;
    const owner = generation.current;
    if (!open) {
      setConfig({ ...emptyNativePostgresConfig, ...initialConfig });
      setTested(null);
      setSelected([]);
      setStep('credentials');
      setError('');
      setGrantError('');
      setSavedItem(existing ?? null);
      setGrantState('paused');
      setBusy(false);
      setQuery('');
      requestId.current = null;
      return () => {
        generation.current += 1;
      };
    }
    setSavedItem(existing ?? null);
    setChoice(nativePostgresChoice(existing ?? null, initialAudience));
    if (!existing)
      return () => {
        generation.current += 1;
      };
    setLoadingExisting(true);
    setError('');
    void companyRequest<NativePostgresDetail>(
      `/api/v1/company/postgres/${encodeURIComponent(existing.connectionId)}`,
    )
      .then((detail) => {
        if (generation.current !== owner) return;
        setConfig((current) => nativePostgresDetailConfig(detail, current.password));
        setSelected(detail.resources);
      })
      .catch((failure) => {
        if (generation.current === owner) setError(nativePostgresError(failure, locale));
      })
      .finally(() => {
        if (generation.current === owner) setLoadingExisting(false);
      });
    return () => {
      generation.current += 1;
    };
  }, [open, existing?.connectionId, scopeKey, initialAudience]);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );
  const tables = useMemo(
    () =>
      tested?.catalog.schemas.flatMap((schema) =>
        schema.tables.map((table) => ({ ...table, schema: schema.name })),
      ) ?? [],
    [tested],
  );
  const supportsWrites = tables.some(
    (table) => table.write && selected.includes(`${table.schema}.${table.name}`),
  );
  const validCredentials = Boolean(
    config.name.trim() &&
    config.host.trim() &&
    config.database.trim() &&
    config.username.trim() &&
    config.password &&
    Number(config.port) > 0,
  );
  function updateConfig(next: Partial<NativePostgresConfig>) {
    requestId.current = null;
    setConfig((current) => ({ ...current, ...next }));
    setTested(null);
    setSelected((current) => (existing ? current : []));
    setStep('credentials');
  }
  async function testConnection() {
    if (!validCredentials || busy || loadingExisting) return;
    const owner = generation.current;
    setBusy(true);
    setError('');
    try {
      const result = await companyRequest<NativePostgresTestResult>(
        '/api/v1/company/postgres/test',
        'POST',
        nativePostgresConfigBody(config),
      );
      if (generation.current !== owner) return;
      setTested(result);
      const available = new Set(
        result.catalog.schemas.flatMap((schema) =>
          schema.tables.map((table) => `${schema.name}.${table.name}`),
        ),
      );
      setSelected((current) =>
        existing ? current.filter((resource) => available.has(resource)) : [],
      );
      setStep('tables');
    } catch (failure) {
      if (generation.current === owner) setError(nativePostgresError(failure, locale));
    } finally {
      if (generation.current === owner) setBusy(false);
    }
  }
  function toggle(resource: string) {
    setSelected((current) =>
      current.includes(resource)
        ? current.filter((item) => item !== resource)
        : [...current, resource],
    );
  }
  function select(resources: string[], checked: boolean) {
    setSelected((current) =>
      checked
        ? [...new Set([...current, ...resources])]
        : current.filter((id) => !resources.includes(id)),
    );
  }
  function back() {
    if (busy) return;
    if (step === 'access') {
      setStep('tables');
      return;
    }
    setTested(null);
    setStep('credentials');
  }
  async function save() {
    if (!tested || !selected.length || busy) return;
    const owner = generation.current;
    setBusy(true);
    setError('');
    setGrantError('');
    try {
      const path = existing
        ? `/api/v1/company/postgres/${encodeURIComponent(existing.connectionId)}`
        : '/api/v1/company/postgres';
      const body = existing
        ? {
            config: nativePostgresConfigBody(config),
            resources: selected,
            expectedCatalogHash: tested.catalogHash,
            expectedBindingVersion: existing.bindingVersion,
          }
        : {
            config: nativePostgresConfigBody(config),
            resources: selected,
            expectedCatalogHash: tested.catalogHash,
            requestId: requestId.current ?? (requestId.current = secureRequestId()),
            audience: choice.audience === 'company' ? 'company' : 'private',
          };
      const result = await companyRequest<{ item: ConnectionReadItem }>(
        path,
        existing ? 'PUT' : 'POST',
        body,
      );
      if (generation.current !== owner) return;
      requestId.current = null;
      setSavedItem(result.item);
      setStep('saved');
      setConfig((current) => ({ ...current, password: '' }));
      try {
        const granted = await updateConnectionPolicy(
          result.item,
          nativePostgresAccessChoice(choice, supportsWrites),
        );
        if (generation.current !== owner) return;
        setGrantState('enabled');
        onSaved?.(granted);
        window.dispatchEvent(new CustomEvent('native-postgres-saved'));
      } catch {
        if (generation.current !== owner) return;
        setGrantState('paused');
        setGrantError(
          'The database was saved, but reading is paused until you allow it. Retry the permission below.',
        );
        onSaved?.(result.item);
        window.dispatchEvent(new CustomEvent('native-postgres-saved'));
      }
    } catch (failure) {
      if (generation.current === owner) setError(nativePostgresError(failure, locale));
    } finally {
      if (generation.current === owner) setBusy(false);
    }
  }
  async function retryGrant() {
    if (!savedItem || busy) return;
    const owner = generation.current;
    setBusy(true);
    setGrantError('');
    try {
      const granted = await updateConnectionPolicy(
        savedItem,
        nativePostgresAccessChoice(choice, supportsWrites),
      );
      if (generation.current !== owner) return;
      setGrantState('enabled');
      onSaved?.(granted);
      window.dispatchEvent(new CustomEvent('native-postgres-saved'));
    } catch {
      if (generation.current === owner)
        setGrantError(
          'Permission could not be enabled yet. Retry when the workspace is available.',
        );
    } finally {
      if (generation.current === owner) setBusy(false);
    }
  }
  async function remove() {
    if (!savedItem || busy) return;
    const owner = generation.current;
    setBusy(true);
    setError('');
    try {
      const result = await companyRequest<{ disconnected: boolean; cleanupPending?: boolean }>(
        `/api/v1/company/postgres/${encodeURIComponent(savedItem.connectionId)}`,
        'DELETE',
      );
      if (generation.current !== owner) return;
      if (result.disconnected) {
        onDeleted?.(
          result.cleanupPending
            ? 'PostgreSQL was disconnected. Operator follow-up is required to finish provider cleanup.'
            : 'PostgreSQL was disconnected.',
        );
        onClose();
      }
    } catch (failure) {
      if (generation.current === owner) setError(nativePostgresError(failure, locale));
    } finally {
      if (generation.current === owner) setBusy(false);
    }
  }
  return {
    people,
    config,
    tested,
    selected,
    choice,
    step,
    grantState,
    busy,
    error,
    grantError,
    query,
    advanced,
    loadingExisting,
    confirmRemove,
    setChoice,
    setQuery,
    setAdvanced,
    setConfirmRemove,
    updateConfig,
    testConnection,
    toggle,
    back,
    save,
    retryGrant,
    remove,
    validCredentials,
    tables,
    select,
    continueToAccess: () => selected.length && setStep('access'),
    supportsWrites,
  };
}
